import Soundfont from 'soundfont-player';
import * as Tone from 'tone';

// Soundfont instrument mappings to high-quality instruments. Hi Aoli
export const soundfontInstruments = {
  piano: 'acoustic_grand_piano',
  electric_piano: 'electric_piano_1',
  strings: 'string_ensemble_1',
  guitar: 'acoustic_guitar_nylon',
  bass: 'acoustic_bass',
  brass: 'trumpet',
  flute: 'flute',
  organ: 'church_organ',
  bells: 'glockenspiel',
  choir: 'choir_aahs',
  harp: 'orchestral_harp',
  saxophone: 'tenor_sax',
  violin: 'violin',
  cello: 'cello',
  clarinet: 'clarinet',
  oboe: 'oboe',
  french_horn: 'french_horn',
  trombone: 'trombone',
  drums: 'synth_drum'
};

// Cache for loaded soundfont instruments
const soundfontCache = new Map();

// Shared output bus for all soundfont instruments. Audibly identical to the
// default direct-to-destination routing, but gives recordings a node to tap.
let soundfontBus = null;

export function getSoundfontBus() {
  const ctx = Tone.context.rawContext;
  if (!soundfontBus || soundfontBus.context !== ctx) {
    soundfontBus = ctx.createGain();
    soundfontBus.connect(ctx.destination);
  }
  return soundfontBus;
}

/**
 * Creates a Tone.js-compatible wrapper around soundfont instruments
 */
class SoundfontWrapper {
  constructor(soundfontInstrument, volume = 0) {
    this.soundfontInstrument = soundfontInstrument;
    this.volume = { value: volume };
    this._disposed = false;
    this.activeNotes = new Map(); // Track active notes for proper release
    
    // Create a fallback synth for tied notes
    this.fallbackSynth = new Tone.Synth({
      oscillator: { type: 'sine' },
      envelope: { 
        attack: 0.01, 
        decay: 0.1, 
        sustain: 0.8, 
        release: 0.3 
      },
      volume: volume
    }).toDestination();
    
    // Boost the master volume if it's too low
    if (Tone.Destination.volume.value < -10) {
      Tone.Destination.volume.value = 0;
    }
  }

  // Tone.js-compatible interface
  triggerAttack(note, time = undefined) {
    if (this._disposed) return;
    
    const velocity = this._volumeToVelocity(this.volume.value);
    const options = { 
      gain: velocity,
      when: time || Tone.now()
      // Let soundfont use its default destination
    };
    
    // Stop any existing note at this pitch first
    this.triggerRelease(note, time);
     
    try {
      const audioNode = this.soundfontInstrument.play(note, Tone.now(), options);
      if (audioNode) {
        this.activeNotes.set(note, audioNode);
      }
    } catch (error) {
    }
  }

  triggerRelease(note, time = undefined) {
    if (this._disposed) return;
    
    const audioNode = this.activeNotes.get(note);
    if (audioNode && audioNode.stop) {
      try {
        audioNode.stop(time || Tone.now() + 0.1); // Small delay for natural release
        this.activeNotes.delete(note);
      } catch (error) {
        // Node might already be stopped
      }
    }
  }

  // `cents` retunes the note away from equal temperament (see utils/tuning.js)
  triggerAttackRelease(note, duration, time = undefined, velocity = 1, cents = 0) {
    if (this._disposed) return;
    
    const startTime = time || Tone.now();
    const adjustedVelocity = this._volumeToVelocity(this.volume.value) * velocity;
    
    try {
      // Always use soundfont for consistent instrument sound
      const audioNode = this.soundfontInstrument.play(note, startTime, { 
        duration: Math.max(duration, 0.1),
        gain: adjustedVelocity,
        cents
      });
      
      if (audioNode) {
        this.activeNotes.set(note + '_' + startTime, audioNode);
      }
    } catch (error) {
    }
  }

  // Release all currently playing notes
  releaseAll(time = undefined) {
    const releaseTime = time || Tone.now();
    this.activeNotes.forEach((audioNode, note) => {
      if (audioNode && audioNode.stop) {
        try {
          audioNode.stop(releaseTime + 0.1);
        } catch (error) {
          // Node might already be stopped
        }
      }
    });
    this.activeNotes.clear();
  }

  // Convert dB volume to velocity (0-1)
  _volumeToVelocity(volumeDb) {
    // Convert dB to linear scale with massive boost for soundfonts
    // Soundfonts tend to be very quiet, so we boost significantly
    const linearVolume = Math.pow(10, volumeDb / 20);
    // Apply a 10x boost for soundfonts + ensure it's audible
    return Math.max(0.2, Math.min(10, linearVolume * 10));
  }

  dispose() {
    this._disposed = true;
    this.releaseAll();
    this.activeNotes.clear();
    
    // Dispose the fallback synth
    if (this.fallbackSynth) {
      this.fallbackSynth.dispose();
    }
    // Soundfont instruments don't need explicit disposal
  }

  // Tone.js compatibility
  connect() {
    return this; // Soundfont already connects to destination
  }

  toDestination() {
    return this; // Soundfont already connects to destination
  }
}

/**
 * Loads a soundfont instrument with caching
 */
/**
 * Synthetic sustained tones with the same interface as the soundfont
 * wrapper. 'sine' is a pure tone: no overtones, so it isolates the pitch
 * itself. 'sawtooth' (through a gentle low-pass) has a full overtone series,
 * which is what makes tuning audible: in a just interval the overtones of
 * the two notes line up and the beating disappears; in equal temperament
 * they beat. A sine pair cannot beat, so for A/B listening use the rich one.
 */
class PureToneInstrument {
  constructor(volume = 0, waveform = 'sine') {
    this.volume = { value: volume };
    this._disposed = false;
    const rich = waveform !== 'sine';
    this.synth = new Tone.PolySynth(Tone.Synth, {
      oscillator: { type: waveform },
      envelope: { attack: 0.02, decay: 0.05, sustain: 0.9, release: 0.25 },
      volume: rich ? volume - 8 : volume // a sawtooth is much louder than a sine
    });
    this.filter = rich ? new Tone.Filter({ type: 'lowpass', frequency: 2600, Q: 0.5 }).toDestination() : null;
    if (this.filter) this.synth.connect(this.filter);
    else this.synth.toDestination();
  }

  _hz(note, cents) {
    return Tone.Frequency(note).toFrequency() * Math.pow(2, cents / 1200);
  }

  triggerAttack(note, time = undefined, cents = 0) {
    if (this._disposed) return;
    this.synth.triggerAttack(this._hz(note, cents), time || Tone.now());
  }

  triggerRelease(note, time = undefined, cents = 0) {
    if (this._disposed) return;
    this.synth.triggerRelease(this._hz(note, cents), time || Tone.now());
  }

  triggerAttackRelease(note, duration, time = undefined, velocity = 1, cents = 0) {
    if (this._disposed) return;
    this.synth.triggerAttackRelease(this._hz(note, cents), Math.max(duration, 0.05), time || Tone.now(), velocity);
  }

  releaseAll(time = undefined) {
    if (this._disposed) return;
    this.synth.releaseAll(time || Tone.now());
  }

  dispose() {
    if (this._disposed) return;
    this._disposed = true;
    this.synth.dispose();
    if (this.filter) this.filter.dispose();
  }
}

export const PURE_TONE = 'pure_tone';
export const RICH_TONE = 'rich_tone';

export function createPureToneInstrument(volume = 0, waveform = 'sine') {
  return new PureToneInstrument(volume, waveform);
}

export async function loadSoundfontInstrument(instrumentName, volume = 0) {
  if (instrumentName === PURE_TONE || instrumentName === RICH_TONE) {
    if (Tone.context.state !== 'running') {
      await Tone.start();
    }
    return createPureToneInstrument(volume, instrumentName === RICH_TONE ? 'sawtooth' : 'sine');
  }
  const soundfontName = soundfontInstruments[instrumentName] || soundfontInstruments.piano;
  
  // Check cache first
  if (soundfontCache.has(soundfontName)) {
    const cachedInstrument = soundfontCache.get(soundfontName);
    return new SoundfontWrapper(cachedInstrument, volume);
  }

  try {
    // Ensure Tone.js context is running
    if (Tone.context.state !== 'running') {
      await Tone.start();
    }
    
    // Use FluidR3_GM for clarinet (better quality), MusyngKite for others
    const soundfontOptions = soundfontName === 'clarinet' 
      ? { soundfont: 'FluidR3_GM' }
      : {};
    
    const soundfontInstrument = await Soundfont.instrument(Tone.context.rawContext, soundfontName, soundfontOptions);

    if (!soundfontInstrument) {
      throw new Error(`Soundfont instrument ${soundfontName} failed to load`);
    }

    // Route through the shared bus so MP3 export can capture soundfont audio
    if (soundfontInstrument.connect) {
      soundfontInstrument.connect(getSoundfontBus());
    }

    // Cache the loaded instrument
    soundfontCache.set(soundfontName, soundfontInstrument);
    
    const wrapper = new SoundfontWrapper(soundfontInstrument, volume);
    
    return wrapper;
  } catch (error) {
    // Fallback to Tone.js synth
    return new Tone.Synth({ volume }).toDestination();
  }
}

/**
 * Preloads common instruments for better performance
 */
export async function preloadCommonInstruments() {
  const commonInstruments = ['piano', 'guitar', 'strings', 'brass', 'flute'];
  
  const loadPromises = commonInstruments.map(async (instrumentName) => {
    try {
      await loadSoundfontInstrument(instrumentName);
    } catch (error) {
    }
  });

  await Promise.allSettled(loadPromises);
}

/**
 * Clears the soundfont cache
 */
export function clearSoundfontCache() {
  soundfontCache.clear();
}

/**
 * Boost system volume for better soundfont playback
 */
export function boostSystemVolume() {
  // Boost Tone.js destination volume
  Tone.Destination.volume.value = Math.max(0, Tone.Destination.volume.value);
  
  // Boost the browser's master volume if available
  if (Tone.context.rawContext.destination.gain) {
    Tone.context.rawContext.destination.gain.value = Math.max(1.0, Tone.context.rawContext.destination.gain.value);
  }
}